import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { ClickAwayListener, Drawer, IconButton } from '@mui/material';
import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import LoadingComponent from 'components/LoadingComponent';
import * as React from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { fetchWatchList, WatchList } from 'services/clients/watchList';
import { useSegment } from 'common/hooks/segment';
import { SHIPMENTS_QUERY_PARAMS } from '../../params';
import { Header } from './header';
import { Sections } from './sections';

interface Props {
  shipmentsQuery: UseQueryResult<WatchList>;
}

export const DetailDrawer: React.FC<Props> = (props) => {
  const { searchParams, setSearchParams } = useCustomSearchParams(SHIPMENTS_QUERY_PARAMS);
  const { shipmentsQuery } = props;
  const { focusedContainer } = searchParams;

  const containerFromShipment = shipmentsQuery.data?.results.find(
    (c) => c.containerNumber === focusedContainer
  );

  const { track } = useSegment();

  const [shouldRefresh, setShouldRefresh] = React.useState(false);

  const request = useQuery(
    ['watchList', focusedContainer, shouldRefresh],
    ({ signal }) =>
      fetchWatchList({
        filters: {
          containerNumbers: [focusedContainer],
          onlyShipments: true,
          sortBy: 'created_at',
          unwatched: null,
        },
        signal,
      }),
    {
      enabled: (!!focusedContainer && shouldRefresh) || !containerFromShipment,
      onSuccess: () => setShouldRefresh(false),
    }
  );

  React.useEffect(() => {
    if (focusedContainer) {
      track(
        'Container side drawer opened',
        { containerNumber: focusedContainer },
        { includeUser: true, includeCompany: true }
      );
    }
  }, [focusedContainer, track]);

  const container = containerFromShipment || request.data?.results[0];

  const close = () => setSearchParams({ focusedContainer: null });

  let inner: React.ReactNode;

  if (shipmentsQuery.isLoading) {
    inner = (
      <div className="w-full h-full flex items-center justify-center">
        <LoadingComponent isLoading size={55} />
      </div>
    );
  } else if (container) {
    inner = (
      <Sections container={container}>
        <div className="px-4 py-6">
          <Header container={container} />
        </div>
      </Sections>
    );
  } else {
    inner = (
      <div className="w-full h-full flex items-center justify-center">
        <h1>Container not found…</h1>
      </div>
    );
  }

  return (
    <ClickAwayListener onClickAway={close}>
      <Drawer
        anchor="right"
        open={!!focusedContainer}
        onClose={close}
        hideBackdrop
        variant="persistent"
        classes={{
          paper: 'shadow-md w-[80vw] h-full flex flex-col',
        }}
      >
        <IconButton
          className="p-0 mx-4 my-2 hover:bg-gray-100 bg-unset rounded-sm self-start"
          onClick={close}
          disableRipple
        >
          <KeyboardDoubleArrowRightIcon />
        </IconButton>
        {inner}
      </Drawer>
    </ClickAwayListener>
  );
};
